import LegalLayout from "../components/LegalLayout";

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      lastUpdated="Effective Date: May 2, 2026 · Last Updated: May 2, 2026"
    >
      <p>
        eGeez Inc. (&ldquo;eGeez,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) is a Delaware corporation
        with its registered address at 8 The Green, Suite A, Dover, DE 19901, United States. eGeez
        and the eGeez logo are registered trademarks of eGeez Inc., registered with the United
        States Patent and Trademark Office (USPTO) and in other jurisdictions worldwide.
      </p>
      <p>
        This Privacy Policy governs the collection, use, disclosure, and protection of personal
        information across all eGeez platforms including the eGeez licensed marketplace
        application, the eLicensee portal, and the eGeez website at egeez.com (collectively the
        &ldquo;Platform&rdquo;).
      </p>
      <p>
        By accessing or using our Platform, you acknowledge that you have read, understood, and
        agree to the terms of this Privacy Policy.
      </p>

      <h2><span className="doc-section-num">1.</span>Introduction</h2>
      <p>
        This Privacy Policy applies to all users of the eGeez Platform — fans, licensees, licensors,
        and visitors. Capitalised terms not defined here have the meaning given in our Terms of Use.
      </p>

      <h2><span className="doc-section-num">2.</span>Information We Collect</h2>
      <h3>2.1 Information You Provide Directly</h3>
      <ul>
        <li>Full name, email address, phone number, and account credentials</li>
        <li>Business information including company name, domain, and licensing details</li>
        <li>Payment and billing information processed securely through our payment partners</li>
        <li>Licensing agreements and territory authorizations uploaded to the Platform</li>
        <li>Product information submitted through the eLicensee portal</li>
        <li>Communications with our team including support inquiries and onboarding interactions</li>
      </ul>
      <h3>2.2 Information Collected Automatically</h3>
      <ul>
        <li>Device information including browser type, operating system, and IP address</li>
        <li>Usage data including pages visited, features accessed, clicks, and time spent on Platform</li>
        <li>Geolocation data used strictly for territory verification and licensed product geofencing</li>
        <li>Cookie and tracking technology data as described in Section 8 below</li>
        <li>Log files and diagnostic data generated during Platform use</li>
      </ul>
      <h3>2.3 Information From Third Parties</h3>
      <ul>
        <li>Publicly available licensee domain and business information used for our Licensed Products Intelligence system</li>
        <li>Licensing database information used for ePIN verification and licensee authentication through our eSmart Gateway</li>
        <li>Information from licensing bodies and industry databases to verify licensor and licensee credentials</li>
      </ul>

      <h2><span className="doc-section-num">3.</span>How We Use Your Information</h2>
      <p>We use your personal information to:</p>
      <ul>
        <li>Provide, operate, maintain, and improve the eGeez Platform and all its features</li>
        <li>Verify licensee identity and licensing status through our proprietary eSmart Gateway verification system</li>
        <li>Generate and assign unique ePINs to verified licensees and licensors</li>
        <li>Enforce territory-based purchasing restrictions in strict accordance with uploaded licensing agreements</li>
        <li>Process transactions and facilitate royalty tracking, reporting, and payment through eGeezPay</li>
        <li>Send transactional communications including account updates, verification notices, and order confirmations</li>
        <li>Send marketing communications where you have provided explicit consent</li>
        <li>Detect, prevent, and investigate fraud, counterfeiting, unauthorized licensing, and territory violations</li>
        <li>Power our Licensed Consumer Products Intelligence system through aggregated and anonymized data analysis</li>
        <li>Comply with applicable laws, regulations, and legal obligations</li>
        <li>Respond to legal requests from competent authorities where required</li>
      </ul>

      <h2><span className="doc-section-num">4.</span>Legal Basis For Processing (GDPR)</h2>
      <p>
        For users located in the European Economic Area (EEA) or United Kingdom, we process
        personal information under the following lawful bases:
      </p>
      <ul>
        <li><strong>Contract performance</strong> — to fulfill our obligations to you as a registered user of the Platform</li>
        <li><strong>Legitimate interests</strong> — to operate, secure, improve, and protect our Platform and its users</li>
        <li><strong>Legal compliance</strong> — to meet our applicable regulatory and legal obligations</li>
        <li><strong>Consent</strong> — for marketing communications, non-essential cookies, and optional data processing activities</li>
      </ul>
      <p>
        You may withdraw consent at any time without affecting the lawfulness of processing carried
        out prior to withdrawal.
      </p>

      <h2><span className="doc-section-num">5.</span>Sharing Your Information</h2>
      <p>
        eGeez does not sell, rent, or trade your personal information to third parties. We may share
        your information only in the following circumstances:
      </p>
      <ul>
        <li><strong>Service providers</strong> who assist in operating our Platform including cloud infrastructure, payment processing, analytics, customer support tools, and fraud prevention</li>
        <li><strong>Licensors and licensing bodies</strong> where disclosure is required to verify licensing compliance, territory authorization, or royalty reporting</li>
        <li><strong>Legal authorities</strong> where required by applicable law, court order, subpoena, or governmental regulation</li>
        <li><strong>Business transfers</strong> in connection with a merger, acquisition, or sale of assets, where your information may be transferred as part of that transaction</li>
        <li><strong>With your consent</strong> for any other purpose not described in this Policy</li>
      </ul>
      <p>
        All third-party service providers are contractually obligated to protect your information
        and use it solely for the purposes for which it was disclosed.
      </p>

      <h2><span className="doc-section-num">6.</span>Data Retention</h2>
      <p>
        We retain your personal information for as long as your account is active or as needed to
        provide our services. We will retain and use your information as necessary to:
      </p>
      <ul>
        <li>Comply with our legal obligations</li>
        <li>Resolve disputes and enforce our agreements</li>
        <li>Meet licensing compliance and royalty audit requirements</li>
      </ul>
      <p>
        Upon account termination or valid erasure request, we will delete or anonymize your personal
        information within 90 days, except where retention is required by law.
      </p>

      <h2><span className="doc-section-num">7.</span>Your Rights</h2>
      <h3>For EEA and UK residents (GDPR)</h3>
      <ul>
        <li>Right to access your personal information</li>
        <li>Right to rectification of inaccurate data</li>
        <li>Right to erasure (&ldquo;right to be forgotten&rdquo;)</li>
        <li>Right to restriction of processing</li>
        <li>Right to data portability</li>
        <li>Right to object to processing</li>
        <li>Right to withdraw consent at any time</li>
      </ul>
      <h3>For California residents (CCPA)</h3>
      <ul>
        <li>Right to know what personal information is collected</li>
        <li>Right to know whether personal information is sold or disclosed</li>
        <li>Right to opt out of the sale of personal information</li>
        <li>Right to access your personal information</li>
        <li>Right to deletion of personal information</li>
        <li>Right to non-discrimination for exercising your rights</li>
      </ul>
      <h3>For all users</h3>
      <ul>
        <li>Right to opt out of marketing communications at any time</li>
        <li>Right to request correction of inaccurate information</li>
      </ul>
      <p>To exercise any of these rights please contact us at the details in Section 13.</p>

      <h2><span className="doc-section-num">8.</span>Cookies and Tracking Technologies</h2>
      <p>
        eGeez uses cookies and similar tracking technologies to operate and improve our Platform.
        We use the following categories of cookies:
      </p>
      <ul>
        <li><strong>Essential cookies</strong> — required for the Platform to function correctly including authentication, session management, and territory detection</li>
        <li><strong>Analytics cookies</strong> — used to understand how users interact with our Platform including Google Analytics 4</li>
        <li><strong>Preference cookies</strong> — used to remember your settings and preferences</li>
        <li><strong>Marketing cookies</strong> — used where you have consented to receive targeted communications</li>
      </ul>
      <p>
        You may control cookie preferences through your browser settings. Disabling essential
        cookies may affect Platform functionality. For more information visit{" "}
        <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer">
          www.allaboutcookies.org
        </a>.
      </p>

      <h2><span className="doc-section-num">9.</span>Data Security</h2>
      <p>
        eGeez implements industry-standard technical and organizational security measures to protect
        your personal information against unauthorized access, alteration, disclosure, or
        destruction. These measures include:
      </p>
      <ul>
        <li>Encrypted data transmission using SSL/TLS protocols</li>
        <li>Secure cloud infrastructure with access controls</li>
        <li>Regular security assessments and monitoring</li>
        <li>Restricted internal access to personal information on a need-to-know basis</li>
      </ul>
      <p>
        While we take all reasonable precautions, no method of transmission over the internet is
        100% secure. We encourage you to use strong passwords and protect your account credentials.
      </p>

      <h2><span className="doc-section-num">10.</span>International Data Transfers</h2>
      <p>
        eGeez operates globally. Your personal information may be transferred to and processed in
        countries outside your country of residence including the United States. Where we transfer
        data from the EEA or UK, we ensure appropriate safeguards are in place in accordance with
        applicable data protection laws including Standard Contractual Clauses approved by the
        European Commission.
      </p>

      <h2><span className="doc-section-num">11.</span>Children&rsquo;s Privacy</h2>
      <p>
        The eGeez Platform is not directed to individuals under the age of 13. We do not knowingly
        collect personal information from children under 13. If you are a parent or guardian and
        believe your child has provided us with personal information, please contact us immediately
        at <a href="mailto:support@egeez.com">support@egeez.com</a> and we will take steps to delete
        such information promptly.
      </p>

      <h2><span className="doc-section-num">12.</span>Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time to reflect changes in our practices,
        technology, legal requirements, or other factors. We will notify registered users of
        material changes via email or prominent notice on the Platform. Your continued use of the
        Platform following notification constitutes acceptance of the updated Policy.
      </p>

      <h2><span className="doc-section-num">13.</span>Contact Us</h2>
      <p>
        For any questions, concerns, or requests regarding this Privacy Policy or your personal
        information, please contact us:
      </p>
      <p>
        <strong>eGeez Inc.&trade;</strong><br />
        8 The Green, Suite A<br />
        Dover, DE 19901<br />
        United States<br />
        Email: <a href="mailto:support@egeez.com">support@egeez.com</a><br />
        Website: <a href="https://www.egeez.com" target="_blank" rel="noopener noreferrer">www.egeez.com</a>
      </p>
      <p>
        For EEA residents who are not satisfied with our response, you have the right to lodge a
        complaint with your local data protection supervisory authority. For UK residents, this is
        the Information Commissioner&rsquo;s Office (ICO) at{" "}
        <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>.
      </p>

      <hr />
      <p className="text-black/55">
        <em>eGeez Inc.&trade; — All rights reserved. eGeez is a registered trademark of eGeez Inc.</em>
      </p>
    </LegalLayout>
  );
}
